import React, { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';

const CONFIG_UPDATE_EVENT = 'petrometer:config-updated';
const CONFIG_UPDATE_STORAGE_KEY = 'petrometer:config-updated';
const CONFIG_RELOAD_DELAY_MS = 3000;

// Notifies every open tab/window in this browser that a config change was
// applied, so they all reload and stay in sync with the new values. The
// local CustomEvent covers this tab; the storage write/removal is what
// other same-origin tabs/windows pick up via the native "storage" event.
export function broadcastConfigUpdate(message) {
  window.dispatchEvent(
    new CustomEvent(CONFIG_UPDATE_EVENT, { detail: { message } }),
  );

  try {
    localStorage.setItem(
      CONFIG_UPDATE_STORAGE_KEY,
      JSON.stringify({ message, timestamp: Date.now() }),
    );
    localStorage.removeItem(CONFIG_UPDATE_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable (e.g. private browsing); other tabs
    // just won't be notified in that case.
  }
}

export function ConfigSyncListener() {
  const [configMessage, setConfigMessage] = useState(null);

  useEffect(() => {
    const handleLocalConfigUpdate = (event) => {
      setConfigMessage(event.detail.message);
    };

    const handleStorageEvent = (event) => {
      if (event.key !== CONFIG_UPDATE_STORAGE_KEY || !event.newValue) {
        return;
      }
      try {
        const { message } = JSON.parse(event.newValue);
        setConfigMessage(message);
      } catch {
        // ignore malformed payloads
      }
    };

    window.addEventListener(CONFIG_UPDATE_EVENT, handleLocalConfigUpdate);
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener(CONFIG_UPDATE_EVENT, handleLocalConfigUpdate);
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);

  useEffect(() => {
    if (!configMessage) {
      return undefined;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: () => (
        <div className="react-confirm-alert-body">
          <h1>Configuration updated</h1>
          <p>{configMessage}</p>
          <p>This page will reload automatically to sync the latest configuration.</p>
        </div>
      ),
    });

    const timer = setTimeout(() => {
      window.location.reload();
    }, CONFIG_RELOAD_DELAY_MS);

    return () => clearTimeout(timer);
  }, [configMessage]);

  return null;
}
